import React, { useState, useMemo, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { debounce } from 'lodash'

import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import { useCanEditDeck } from 'redux/deck/selectors'

import { CardType, OWNED, SaveStatus } from 'types/deck'
import { PinnedCardsRecordType } from 'services/apiTypes/pinnedCards.types'

import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import ToggleFoilButton from 'components/card/cardForm/toggleFoilButton'
import QuantityInput from 'components/card/cardForm/quantityInput'
import EditionOptions from 'components/card/cardForm/editionOptions'
import AddToNewDeck from 'components/card/cardForm/addToNewDeck'
import AddToPinsButton from 'components/card/cardForm/addToPins'
import AddCardsModal from 'components/collectionV2/addCards/Modal'
import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'

import styles from './pinnedCardForm.module.scss'

type Props = {
  cardRecord: PinnedCardsRecordType
  onCardUpdated: (card: PinnedCardsRecordType, removeId?: number) => void
  setSaving: (status: SaveStatus | undefined) => void
}

const PinnedCardForm = ({ cardRecord, onCardUpdated, setSaving }: Props) => {
  const starttingRef = React.useRef<HTMLButtonElement>(null)

  const [{ tbId: userId }] = useCookies(['tbId'])

  const canEditDeck = useCanEditDeck()

  const [localNotes, setLocalNotes] = useState(cardRecord.notes)
  const [localQuantity, setLocalQuantity] = useState(cardRecord.quantity)
  const [addToCollection, setAddToCollection] = useState(false)
  const [localCard, setLocalCard] = useState(cardRecord.card)

  useEffect(() => {
    setLocalNotes(cardRecord.notes)
    setLocalQuantity(cardRecord.quantity)
    setLocalCard(cardRecord.card)
    setAddToCollection(false)
  }, [cardRecord.id])

  const handleUpdatePinnedCard = ({
    quantity = 1,
    notes,
    card,
  }: {
    quantity?: number
    notes?: string
    card?: CardType
  }) => {
    const removeId = quantity < 1 ? cardRecord.id : undefined
    const updatedCardRecord: PinnedCardsRecordType = {
      ...cardRecord,
      quantity: quantity || cardRecord.quantity,
      notes: notes || cardRecord.notes,
      card: card || cardRecord.card,
    }

    const request = quantity < 1 ? PinService.remove : PinService.update

    setSaving('saving')
    request(updatedCardRecord)
      .then(() => {
        setSaving('saved')
        onCardUpdated(updatedCardRecord, removeId)
        setLocalCard(updatedCardRecord.card)
        ToastService.create(`${cardRecord.card.name} updated`, 'Pinned Cards', 'success')
      })
      .catch(err => {
        if (localQuantity !== cardRecord.quantity) setLocalQuantity(cardRecord.quantity)
        setSaving('error')
        ToastService.create(err.error, 'Pinned Cards', 'error')
      })
      .finally(() => setSaving(undefined))
  }

  const handleAddToCollection = () => {
    const updatedCard = { ...localCard, owned: OWNED.YES }
    setLocalCard(updatedCard)
    onCardUpdated({ ...cardRecord, card: updatedCard })
  }

  const debouncedQtyChange = useMemo(() => {
    if (cardRecord.quantity !== localQuantity) setLocalQuantity(cardRecord.quantity)
    return debounce(handleUpdatePinnedCard, 660)
  }, [cardRecord])

  const handleQuantityChange = (newQuantity: number) => {
    setLocalQuantity(newQuantity)
    debouncedQtyChange({ quantity: newQuantity })
  }

  return (
    <div className={styles.container}>
      <button className={styles.focusFirstButton} ref={starttingRef} />

      <div className={styles.quantityCommanderRow}>
        <QuantityInput
          className={`${styles.quantityRow}`}
          quantity={cardRecord.quantity}
          onQuantityUpdate={qty => handleQuantityChange(qty)}
        />
      </div>

      <div className={styles.row}>
        <EditionOptions
          label="Printing"
          card={localCard}
          className={styles.editionSelection}
          onEditionChanged={card => handleUpdatePinnedCard({ card })}
        />

        <ToggleFoilButton
          className={''}
          triggerClassName={''}
          card={localCard}
          onCardUpdate={card => handleUpdatePinnedCard({ card })}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.left}>
          <textarea
            className={styles.notesTextArea}
            placeholder="Something something reminder about this card"
            onChange={e => setLocalNotes(e.target.value)}
            onBlur={() =>
              localNotes !== cardRecord.notes ? handleUpdatePinnedCard({ ...cardRecord, notes: localNotes }) : null
            }
            value={localNotes}
          />
          <span className={styles.notesLengthDisplay}>{localNotes.length} / 256</span>
        </div>

        <div className={styles.right}>
          <label>Other options:</label>
          <>
            {!!userId && (
              <PhatButton onClick={() => setAddToCollection(true)}>
                {localCard.owned !== OWNED.NO ? (
                  <span
                    className={`${styles.ownedCollectionDot} ${localCard.owned === OWNED.YES ? styles.greenDot : ''}`}
                  />
                ) : (
                  <Icon name="address book" />
                )}
                Add to collection
              </PhatButton>
            )}
            {!!userId && <AddToOtherDeck card={localCard} />}
            {!!userId && (
              <AddToPinsButton
                card={localCard}
                onCardUpdated={card => handleUpdatePinnedCard({ card })}
                label="Pin card again"
                alwaysAddNewRecord
              />
            )}

            {!canEditDeck && <AddToNewDeck card={localCard} />}
          </>
        </div>
      </div>

      <AddCardsModal
        open={addToCollection}
        cardToAdd={localCard}
        onCardCreatedOrUpdated={handleAddToCollection}
        onClose={() => setAddToCollection(false)}
        closeOnCreateOrUpdate
      />
    </div>
  )
}

export default PinnedCardForm
